/**
 * Images in the marketing builder: the asset manager's upload, wired to the
 * three-step asset API (contract, `docs/api/marketing.md`, "Assets").
 *
 *   1. `createMarketingAssetUpload({fileName, contentType, sizeBytes})`
 *      signs a PUT and creates a `pending` row.
 *   2. PUT the bytes straight to the signed URL.
 *   3. `confirmMarketingAsset(id)` HEADs the object and marks it `ready`.
 *
 * ⛔ STEP TWO IS A PLAIN `fetch` AND NEVER GOES THROUGH `api.ts`'s `request`.
 * That helper JSON-encodes every body (a `File` would upload the string `{}`)
 * and attaches this app's bearer token — a live EMERGE credential sent to
 * somebody else's host for no benefit, since the URL carries its own
 * signature.
 *
 * ⚠️ `Content-Type` on the PUT must be byte-for-byte the string the target was
 * signed with, so it is the value sent in step one rather than re-read later.
 */
import type { Editor, UploadFileFn } from 'grapesjs';
import { api } from '@/lib/api';

/** The server's cap. Checked here first so a refusal costs no request. */
export const MAX_ASSET_BYTES = 5 * 1024 * 1024;

/** The server's allow-list, exactly. */
export const ASSET_TYPES: readonly string[] = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

/** Why a file cannot be uploaded, or `null` when it can. */
export function assetRefusal(file: File): string | null {
  if (!ASSET_TYPES.includes(file.type)) {
    return `${file.name} is not a PNG, JPEG, GIF or WebP image.`;
  }
  if (file.size > MAX_ASSET_BYTES) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return `${file.name} is ${mb} MB; images can be at most 5 MB.`;
  }
  return null;
}

/** One file, end to end. Resolves to the public URL to put in the email. */
export async function uploadAsset(file: File): Promise<{ url: string; name: string }> {
  const contentType = file.type;
  const target = await api.createMarketingAssetUpload({
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
  });
  const { asset, uploadUrl } = target.data;

  const put = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`${file.name} could not be uploaded (storage answered ${put.status}).`);

  const confirmed = await api.confirmMarketingAsset(asset.id);
  return { url: confirmed.data.publicUrl, name: confirmed.data.fileName ?? file.name };
}

/**
 * The asset manager's `uploadFile`. Each file is validated and uploaded on its
 * own, so one oversize image does not stop the three good ones beside it; the
 * refusals and failures are joined into one message for the caller to show.
 *
 * `getEditor` rather than an editor: the config is built before the editor
 * exists.
 */
export function makeUploadFile(
  getEditor: () => Editor | null,
  report: (message: string | null) => void,
): UploadFileFn {
  return async (ev) => {
    const target = ev.target as HTMLInputElement | null;
    const list = ev.dataTransfer?.files ?? target?.files ?? null;
    const files = list ? Array.from(list) : [];
    if (files.length === 0) return;

    const problems: string[] = [];
    const uploads: Promise<void>[] = [];
    let uploaded = 0;
    for (const file of files) {
      const refusal = assetRefusal(file);
      if (refusal) {
        problems.push(refusal);
        continue;
      }
      uploads.push(
        uploadAsset(file)
          .then(({ url, name }) => {
            uploaded += 1;
            getEditor()?.AssetManager.add({ type: 'image', src: url, name });
          })
          .catch((err: unknown) => {
            problems.push(err instanceof Error && err.message ? err.message : `${file.name} could not be uploaded.`);
          }),
      );
    }
    if (uploads.length > 0) report(`Uploading ${uploads.length === 1 ? 'one image' : `${uploads.length} images`}…`);
    await Promise.all(uploads);

    if (problems.length > 0) report(problems.join(' '));
    else if (uploaded > 0) report(uploaded === 1 ? 'Image uploaded.' : `${uploaded} images uploaded.`);
    else report(null);

    // Let a second pick of the same file fire `change` again.
    if (target && 'value' in target) target.value = '';
  };
}

/**
 * Loads the images already uploaded into the asset manager. Only `ready` rows:
 * a `pending` one was never confirmed and its URL may serve nothing.
 */
export async function preloadAssets(editor: Editor, signal: AbortSignal): Promise<void> {
  const res = await api.listMarketingAssets({ page: 1, perPage: 100 }, signal);
  if (signal.aborted) return;
  const ready = res.data
    .filter((a) => a.status === 'ready')
    .map((a) => ({ type: 'image', src: a.publicUrl, name: a.fileName }));
  if (ready.length > 0) editor.AssetManager.add(ready);
}
